import {
  IBuildings,
  IBuildingTypes,
  IListingFloorplans,
  IListingPhotos,
  IOwnershipGroups,
} from '@llws/lift-entity-interfaces';
import { partition } from '@jamesgmarks/utilities';
import {
  ISearchCommandOptions,
  Null,
  Not,
  Equal,
  IQueryCommandOptions,
  IWhereCommand,
  INestedWhereCommand,
  In,
  IOrderCommand,
  GreaterThan,
  convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import { propertyRequest } from '@/shared/request-configs/property-request';
import { IApiResponse, IMetaResponse, mergeQueryCommandOptionsV2 } from '@/shared';
import { baseRequest } from '@/shared/request-configs/base-request';
import config from '@/config';
import vm from '@/main';
import { translationFormatter } from '@/shared/helpers/translation-formatter';
import { newPhotoTranslationSettings } from '@/shared/factory/new-translation-settings';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { getTranslationsByClientIdAndModel } from './translations';

const defaultAlias = 'building';

export const defaultPropertyFilters: IQueryCommandOptions = {
  alias: defaultAlias,
  relations: [
    {
      path: 'building.buildingType',
      alias: 'buildingType',
    },
    {
      path: 'building.city',
      alias: 'city',
    },
    {
      path: 'building.ownershipGroupHasBuildings',
      alias: 'oghb',
    },
    {
      path: 'city.province',
      alias: 'province',
    },
  ],
  select: [
    'assignedPhotoCount',
    'assignedVideoCount',
    'assignedVirtualTourCount',
    'assignedFloorPlansCount',
    'unitTypeCount',
    'unitCount',
    'bsLinked',
  ],
  aggregations: [
    {
      table: 'buildings_has_photos',
      propertyAlias: 'assignedPhotoCount',
      column: 'id',
      aggregate: 'COUNT',
      relations: [
        {
          path: 'listingPhoto',
          alias: 'lp',
        },
      ],
      whereCommands: [
        Equal('buildingId', `${defaultAlias}.id`),
        Not(Equal('lp.active', 0)),
      ],
    },
    {
      table: 'videos',
      propertyAlias: 'assignedVideoCount',
      column: 'id',
      aggregate: 'COUNT',
      whereCommands: [
        Equal('buildingId', `${defaultAlias}.id`),
      ],
    },
    {
      table: 'suite_types',
      propertyAlias: 'unitTypeCount',
      column: 'id',
      aggregate: 'COUNT',
      whereCommands: [
        Equal('buildingId', `${defaultAlias}.id`),
        Not(Equal('status', 'deleted')),
      ],
    },
    {
      table: 'suite_types',
      propertyAlias: 'unitCount',
      column: 'id',
      aggregate: 'COUNT',
      whereCommands: [
        Equal('buildingId', `${defaultAlias}.id`),
        Not(Equal('status', 'deleted')),
        Not(Null('suiteTypeModelId')),
      ],
    },
    {
      table: 'virtual_tours',
      propertyAlias: 'assignedVirtualTourCount',
      column: 'id',
      aggregate: 'COUNT',
      whereCommands: [
        Equal('buildingId', `${defaultAlias}.id`),
        Not(Equal('status', 'deleted')),
      ],
    },
    {
      table: 'listing_floorplans',
      propertyAlias: 'assignedFloorPlansCount',
      column: 'id',
      aggregate: 'COUNT',
      whereCommands: [
        Equal('buildingId', `${defaultAlias}.id`),
        Equal('active', 1),
      ],
    },
    {
      propertyAlias: 'bsLinked',
      table: 'building_stack_buildings',
      aggregate: 'COUNT',
      column: 'id',
      whereCommands: [
        Equal('buildingId', `building.id`),
        Equal('status', 'enabled'),
      ],
    },
  ],
  where: [
    Not(Equal(`${defaultAlias}.status`, 'deleted')),
  ],
};

const propertyAggregateOrderValues: any = {
  photos: '$(count:galleryPhotos.id)',
  units: '$(count:suiteTypes.id|status~not:deleted)',
};
/**
 * Merges filters & defaultPropertyFilters together
 * @param {any} [filters] - optional filters query
 */
const processPropertyFilters = (filters?: Record<string, unknown>) => {
  const query: IQueryCommandOptions = {};

  if (filters) {
    if (filters.buildingName) {
      query.where = [ ...query.where || [], ...[
        {
          condition: 'and',
          whereCommands: [
            {
              field: 'buildingName',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'streetNumber',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'streetName',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'buildingType.name',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'city.cityName',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'contactName',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'phone',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'email',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'adminEmail',
              value: filters.buildingName,
              operator: 'like',
              condition: 'or',
            },
          ] as IWhereCommand[],
        } as INestedWhereCommand,
      ]];
    }

    if (filters.cityId) {
      if (Array.isArray(filters.cityId)) {
        query.where = [ ...query.where || [], ...[In('cityId', filters.cityId.toString().split(','))] ];
      } else {
        query.where = [ ...query.where || [], ...[Equal('cityId', filters.cityId)] ];
      }
    }

    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query.where = [ ...query.where || [], ...[In('status', filters.status.toString().split(','))] ];
      } else {
        query.where = [ ...query.where || [], ...[Equal('status', filters.status)] ];
      }
    }

    if (filters.featured) {
      query.where = [ ...query.where || [], ...[In('featured', (filters.featured as string).split(','))] ];
    }

    if (filters.type) {
      query.where = [ ...query.where || [], ...[Equal('buildingType.categoryType', filters.type)] ];
    }

    if (filters.suiteTypes) {
      query.relations = [{ path: 'suiteTypes', alias: 'st', whereCommands: [Not(Equal('status', 'deleted'))] }];
    }

    if (filters.suiteTypesBed) {
      query.where = [ ...query.where || [], Equal('st.bed', filters.suiteTypesBed) ];
    }

    if (filters.suiteTypesAvailability) {
      if (Array.isArray(filters.suiteTypesAvailability)) {
        query.where = [ ...query.where || [], In('st.available', filters.suiteTypesAvailability.toString().split(',')) ];
      } else {
        query.where = [ ...query.where || [], Equal('st.available', filters.suiteTypesAvailability) ];
      }
    }

    if (filters.suiteTypesBath) {
      query.where = [ ...query.where || [], Equal('st.bath', filters.suiteTypesBath) ];
    }

    if (filters.orderBy) {
      const orderCols = (filters.orderBy as string).split(',');

      orderCols.forEach((o: string) => {
        if (!query.orderBy) query.orderBy = [];

        query.orderBy.push(
          {
            field: propertyAggregateOrderValues[o] || o,
            direction: (filters.orderDir as IOrderCommand['direction'] || 'asc'),
          },
        );
      });
    }

    if (filters.limit) {
      query.limit = Number(filters.limit);
    }

    if (filters.page) {
      query.page = Number(filters.page);
    }
  }

  return convertQueryCommandToQueryString(mergeQueryCommandOptionsV2([defaultPropertyFilters, query]));
};
/**
 * Fetches properties <br>
 * @see processPropertyFilters
 * @see propertyRequest.get
 * @param {any} filters - optional filters query
 */
export const get = (filters: Record<string, unknown>) => propertyRequest.get('', processPropertyFilters(filters));
/**
 * Fetches properties v2 <br>
 * @see propertyRequest.get
 * @param {IQueryCommandOptions} [filters] - optional filters query
 */
export const getV2 = (filters?: IQueryCommandOptions) => {
  const queryString = filters ? convertQueryCommandToQueryString(filters) : '';
  return propertyRequest.get('', queryString);
};
/**
 * Fetches Relations by id <br>
 * API Endpoint - `/${id}/relations` via {@link propertyRequest.get}
 * @param {number} id - id of the relations
 */
export const getRelations = async (id: number) => ((await propertyRequest.get(`/${id}/relations`)).data);
/**
 * Fetches Properties by Relations ID <br>
 * API Endpoint - `/${id}` via {@link propertyRequest.get}
 * @see getRelations
 * @param {number} id - id of the relations
 */
export const getById = async (id: number) => {
  const property = await propertyRequest.get(`/${id}`);
  const relations = await getRelations(id);
  return {
    ...property,
    ...{
      data: {
        ...property.data,
        ...relations,
      },
    },
  };
};
/**
 * Fetches Properties by Relations ID V2 <br>
 * API Endpoint - `/relations/${id}` via {@link propertyRequest.get}
 * @param {number} id - id of the relations
 */
export const getByIdV2 = async (id: number) => {
  const property: IApiResponse<IBuildings, any> = await propertyRequest.get(`/relations/${id}`);
  property.data!.suiteTypes = [];
  return property;
};
/**
 * Fetches Data for Subheader <br>
 * API Endpoint - '/${queryV2}' via {@link propertyRequest.get}
 * @param {(number | string)} id - id of the unit
 */
export const getForSubheader = async (id: number | string) => {
  const unitId = vm()?.$route.params || null;

  const queryV2: IQueryCommandOptions = {
    alias: 'building',
    select: ['id', 'buildingName', 'streetNumber', 'streetName', 'childClientId', 'clientId' ],
    relations: [
      {
        path: 'buildingType',
        alias: 'buildingType',
      },
      {
        path: 'buildingStackBuilding',
        alias: 'buildingStackBuilding',
      },
      {
        path: 'buildingsHasPhotos',
        alias: 'bhp',
        whereCommands: [Equal('orderBy', 'minPhotoOrderBy')],
      },
      {
        path: 'bhp.listingPhoto',
        alias: 'bhplp',
        whereCommands: [Equal('active', '1')],
      },
      ...unitId ? [
        {
          path: 'suiteTypes',
          alias: 'suiteTypes',
          whereCommands: [Equal('id', unitId)],
        },
        {
          path: 'suiteTypes.buildingStackSuiteType',
          alias: 'bstkSuiteType',
        },
      ] : [],
    ],

    aggregations: [
      {
        aggregate: 'MIN',
        table: 'buildings_has_photos',
        column: 'orderBy',
        propertyAlias: 'minPhotoOrderBy',
        whereCommands: [
          Equal('buildingId', 'building.id'),
          Equal('lp.active', '1'),
        ],
        relations: [
          {
            path: 'listingPhoto',
            alias: 'lp',
          },
        ],
      },
    ],
  };
  const response = await propertyRequest.get(`/${id}${convertQueryCommandToQueryString(queryV2)}`);
  return response;
};

/**
 * Fetches Properties List Along with Enabled Unit Count <br>
 * API Endpoint - '/${query}' via {@link propertyRequest.get}
 */
export const getPropertiesListWithEnabledUnitCount = async () => {
  const query: IQueryCommandOptions = {
    alias: 'building',
    select: ['id', 'buildingName', 'enabledUnitCount'],
    where: [Not(Equal('status', 'deleted')), GreaterThan('enabledUnitCount', 0)],
    aggregations: [{
      table: 'suite_types',
      propertyAlias: 'enabledUnitCount',
      column: 'id',
      aggregate: 'COUNT',
      whereCommands: [
        Equal('buildingId', `building.id`),
        Equal('status', 'enabled'),
      ],
    }],
    orderBy: [{ field: 'buildingName', direction: 'asc' }],
    limit: 9999,
  };
  const response = await propertyRequest.get(`/${convertQueryCommandToQueryString(query)}`);
  return response;
};
/**
 * Fetches Properties List <br>
 * API Endpoint - '/${query}' via {@link propertyRequest.get}
 * @param {(number | string)} id - id of the unit
 */
export const getUnitDataForSubheader = async (id: number | string) => {
  const unitId = vm()?.$route.params?.unitId || null;

  const queryV2: IQueryCommandOptions = {
    alias: 'building',
    select: ['id', 'buildingName', 'streetNumber', 'streetName', 'childClientId', 'clientId' ],
    relations: [
      {
        path: 'buildingType',
        alias: 'buildingType',
      },
      ...unitId ? [
        {
          path: 'suiteTypes',
          alias: 'suiteTypes',
          whereCommands: [Equal('id', unitId)],
        },
        {
          path: 'suiteTypes.buildingStackSuiteType',
          alias: 'buildingStackSuiteType',
        },
      ] : [],
    ],
  };

  const response = await propertyRequest.get(`/${id}${convertQueryCommandToQueryString(queryV2)}`);
  return response;
};
/**
 * Fetches Properties List <br>
 * API Endpoint - '/${query}' via {@link propertyRequest.post}
 */
export const getPropertiesList = async () => propertyRequest.search({
  alias: 'p',
  select: [
    'p.id',
    'p.buildingName',
    'p.streetNumber',
    'p.streetName',
    'p.billingTypeId',
    'bt.name',
    'p.buildingHeader',
    'p.intersection',
    'p.neighbourhood',
    'p.contactName',
    'p.phone',
    'p.extension',
    'c.id',
    'c.cityName',
    'pr.id',
    'pr.provinceCode',
  ],
  relations: [
    {
      path: 'p.city',
      alias: 'c',
    },
    {
      path: 'c.province',
      alias: 'pr',
    },
    {
      path: 'p.buildingType',
      alias: 'bt',
    },
  ],
  where: [Not(Equal('p.status', 'deleted'))],
  orderBy: [{ field: 'p.buildingName', direction: 'asc' }],
  limit: 9999,
}) as IApiResponse<IBuildings[], IMetaResponse>;

/**
 * Fetches Settings <br>
 * API Endpoint - '/client-settings' via {@link propertyRequest.get}
 */
export const getSettings = () => propertyRequest.get('/settings');
/**
 * Fetches Client Settings <br>
 * API Endpoint - '/client-settings' via {@link propertyRequest.get}
 */
export const getSettingsV2 = () => propertyRequest.get('/client-settings');
/**
 * Fetches Unit Models <br>
 * API Endpoint - `/property-api/units?relations=building:b,listingPhotos:lp${where}${pages}${orderBy}` via {@link baseRequest.post}
 * @param {(number | string)} propertyId - id of the property
 * @param {any} [filters] - optional filter query string
 */
export const getUnitModels = (
  propertyId?: number | string,
  filters?: any,
) => {
  const searchCommands: ISearchCommandOptions = {
    alias: 'um',
    where: [
      Null('um.suiteTypeModelId'),
      Not(Equal('um.status', 'deleted')),
    ],
    relations: [
      {
        path: 'um.listingPhotos',
        alias: 'lp',
      },
      ...(!propertyId ? [{
        path: 'um.building',
        alias: 'b',
      }] : []),
    ],
  };

  if (filters) {
    if (filters.typeName) {
      searchCommands.where?.push({
        field: 'typeName',
        value: filters.typeName,
        operator: 'like',
      });
    }
    if (filters.searchValue) {
      searchCommands.where?.push(
        {
          condition: 'and',
          whereCommands: [
            {
              field: 'typeName',
              value: filters.searchValue,
              operator: 'like',
              condition: 'or',
            },
            {
              field: 'number',
              value: filters.searchValue,
              operator: 'like',
              condition: 'or',
            },
          ],
        },
      );
    }
    if (filters.limit) {
      searchCommands.limit = filters.page;
    }
    if (filters.page) {
      searchCommands.page = filters.page;
    }
    if (filters.orderBy) {
      // this wasn't working for some reason, need to resolve later, its working with getUnits
      // orderBy = `&orderBy=${filters.orderBy}~${filters.orderDir}`;
    }
  }

  if (propertyId) {
    return propertyRequest.get(`/${propertyId}/units${convertQueryCommandToQueryString(searchCommands)}`);
  }
  // get all unit models, without property scope, but provide building relation data
  return baseRequest.post(`${config?.adminGateway}/v1/property-api/suite_types/_search`, searchCommands);
};
/**
 * Fetches Units of the specified property <br>
 * API Endpoint - `/${propertyId}/units${filters}` via {@link propertyRequest.get}
 * @param {number} propertyId - id of the property
 */
export const getUnits = (propertyId: number) => {
  const filters: IQueryCommandOptions = {
    select: ['id', 'typeName', 'bed', 'bath', 'sqFt', 'rate', 'status', 'number', 'available', 'availabilityDate', 'buildingId'],
    where: [
      Not(Equal('status', 'deleted')),
    ],
    relations: [
      {
        path: 'suitesHasPhotos',
        alias: 'shp',
      },
      {
        path: 'shp.listingPhoto',
        alias: 'shplp',
      },
    ],
    limit: 9999,
  };
  return propertyRequest.get(`/${propertyId}/units${convertQueryCommandToQueryString(filters)}`);
};
/**
 * Saves single property <br>
 * API Endpoint- `/${payload.id}` via {@link propertyRequest.put} <br> or '' via {@link propertyRequest.post}
 * @param {IBuildings} payload - property to save
 */
export const save = async (payload: IBuildings) => {
  const result = payload.id
    ? await propertyRequest.put(`/${payload.id}`, payload)
    : await propertyRequest.post('', payload);

  return result.data ? result.data : result;
};
/**
 * Saves multiple properties <br>
 * @see propertyRequest.put
 * @see propertyRequest.post
 * @param {IBuildings[]} properties - buildings to save
 */
export const saveMany = async (properties: IBuildings[]) => {
  const [
    propertiesWithIds, // Use PUT
    propertiesWithoutIds, // Use POST
  ] = partition(properties, (p) => !!p.id);

  const postResult = (
    propertiesWithoutIds.length > 0
      ? await propertyRequest.post('/', propertiesWithoutIds)
      : []
  ) as IApiResponse<IBuildings[], IMetaResponse>;
  const putResult = (
    propertiesWithIds.length > 0
      ? await propertyRequest.putAll(propertiesWithIds)
      : []
  ) as IApiResponse<IBuildings[], IMetaResponse>;

  return { postResult, putResult };
};
/**
 * Removes by ids <br>
 * @see propertyRequest.delete
 * @param {number[]} ids - array of ids to delete
 */
export const remove = (ids: number[]) => propertyRequest.delete('', ids);

/**
 * Saves Relation based on method <br>
 * API Endpoints:
 * <li>`/${propertyId}/${relation}?${(querystring || '')}` <br> via {@link propertyRequest.post}</lI>
 * <li>`/${propertyId}/${relation}?${(querystring || '')}` <br> via {@link propertyRequest.patch}</lI>
 * <li>`/${propertyId}/${relation}?${(querystring || '')}` <br> via {@link propertyRequest.put}</lI>
 * @param {('post' | 'patch' | 'put')} method - decides what request would be post/patch/put
 * @param {number} propertyId - id of the property
 * @param {string} relation - relation to save
 * @param {any[]} data - data of the relation
 * @param {string} [querystring] - optional filter querystirng
 */
export const saveRelation = (
  method: 'post' | 'patch' | 'put',
  propertyId: number,
  relation: string,
  data: Array<any>,
  querystring?: string,
) => propertyRequest[method](`/${propertyId}/${relation}?${(querystring || '')}`, data);

/**
 * Removes Relation from the specified property <br>
 * API Endpoint - `/${propertyId}/${relation}` <br> via {@link propertyRequest.delete}
 * @param {number} propertyId - id of the property
 * @param {string} relation - relation to remove
 * @param {number[]} data - data
 */
export const deleteRelation = (
  propertyId: number,
  relation: string,
  data: number[],
) => propertyRequest.delete(`/${propertyId}/${relation}`, data);

/**
 * Saves uploaded file results
 * @param {('listing-photos' | 'floorplans')} type - type of file results (listing-photos/floorplans)
 * @param {number} propertyId - id of the property
 * @param {number} clientId - id of the client
 * @param {any[]} imgData - data of the img
 * @see saveRelation
 */
export const saveUploadedFileResults = (
  type: 'listing-photos' | 'floorplans',
  propertyId: number,
  clientId: number,
  imgData: any[],
) => {
  const data: IListingFloorplans[] | IListingPhotos[] = [];

  imgData.forEach((img: any) => {
    if (type === 'listing-photos') {
      const newPhoto: IListingPhotos = {
        clientId,
        buildingId: propertyId,
        image: img.filename,
        width: img.width,
        height: img.height,
        orderBy: 0,
        mainGallery: 1,
        active: 1,
        created: new Date(),
        searchResult: 0,
      };

      (data as IListingPhotos[]).push(newPhoto);
    } else {
      const newFloorplan: IListingFloorplans = {
        clientId,
        buildingId: propertyId,
        image: !img.filename.endsWith('pdf') ? img.filename : '',
        imageWidth: !img.filename.endsWith('pdf') ? img.width : '',
        imageHeight: !img.filename.endsWith('pdf') ? img.height : '',
        pdf: img.filename!.endsWith('pdf') ? img.filename : undefined,
        orderBy: 0,
        active: 1,
        created: new Date(),
        name: '',
      };

      (data as IListingFloorplans[]).push(newFloorplan);
    }
  });

  return saveRelation('post', propertyId, type, data);
};

// export const getUnits = (propertyId: number) => propertyRequest.get(`/${propertyId}/units?view=summary`);
/**
 * Fetches Photos for property id <br>
 * API Endpoint - `/${propertyId}/photos?view=edit` <br> via {@link propertyRequest.get}
 * @see getTranslationsByClientIdAndModel
 * @param {(number | string )} propertyId - id of the property
 */
export const getPhotos = async (propertyId: number | string) => {
  const result = await propertyRequest.get(`/${propertyId}/photos?view=edit`);
  const app = vm();

  if (result.data?.available.length > 0 && app && app.$rsGlobals.$siteSourceClientLanguages.length > 0) {
    const translations = await getTranslationsByClientIdAndModel(app.$rsGlobals.$currentClient.id!, 'photos');

    result.data.available = translationFormatter<IListingPhotos>(
      result.data.available,
      translations,
      app.$rsGlobals.$siteSourceClientLanguages,
      newPhotoTranslationSettings(app.$rsGlobals.$currentClient.id!),
    );
  }

  return result;
};

/**
 * Fetches Property options <br>
 * API Request {@link propertyRequest.get}
 * @param {number[]} [buildingIds] - if provided, will filter the result by building ids
 */
export const getPropertyOptions = async (buildingIds?: number[]) => {
  const query: IQueryCommandOptions = {
    where: [
      Not(Equal('status', 'deleted')),
      ...(buildingIds ? [In('id', buildingIds)] : []),
    ],
    select: [ 'id', 'buildingName' ],
    limit: 9999,
    orderBy: [
      {
        field: 'buildingName',
        direction: 'asc',
      },
    ],
  };
  return propertyRequest.get(convertQueryCommandToQueryString(query)) as IApiResponse<IBuildings[], any>;
};

/**
 * Process Ownership Group <br>
 * Fetches existing result -> if empty make a post request w/ the ownership group <br>
 * API Endpoints:
 * <li>`/property-api/ownership-groups?where=name:${ownershipGroup.name},client.freshbooksClientId:${(ownershipGroup as any).freshbooksClientId}`<br> via {@link adminRequest.get}</li>
 * <li>'/property-api/ownership-groups'<br> via {@link adminRequest.post}</li>
 * @param {IOwnershipGroups} ownershipGroup - ownership group to process
 */
export const processOwnershipGroup = async (ownershipGroup: IOwnershipGroups) => {
  const existingResult = await adminRequest.get(
    `/property-api/ownership-groups?where=name:${ownershipGroup.name},`
    + `client.freshbooksClientId:${(ownershipGroup as any).freshbooksClientId}`, // TODO: Remove conversion to `any` when `freshbooksClientId` is added to the `IOwnershipGroups` interface.
  );

  if (existingResult.data.length > 0) {
    return existingResult.data[0];
  }

  const insertResult = await adminRequest.post('/property-api/ownership-groups', ownershipGroup);
  return insertResult.data[0];
};

/**
 * Fetches Building Types <br>
 * API Endpoint - '/property-api/building-types?limit=999' via {@link adminRequest.get}
 */
export const getBuildingTypes = async (): Promise<IBuildingTypes[]> => (
  await adminRequest.get('/property-api/building-types?limit=999')
).data || [];

/**
 * Fetches Example Building via {@link propertyRequest.get}
 */
export const getExampleBuilding = async () => {
  const filters: IQueryCommandOptions = {
    limit: 1,
    where: [
      {
        field: 'status',
        operator: '',
        value: 'enabled',
        condition: 'and',
        notOperator: false,
      },
    ],
    relations: [
      {
        path: 'suiteTypes',
        alias: 'st',
        whereCommands: [
          {
            field: 'status',
            operator: '',
            value: 'enabled',
            condition: 'and',
            notOperator: false,
          },
          {
            field: 'available',
            operator: '',
            value: '1',
            condition: 'and',
            notOperator: false,
          },
        ],
      },
      {
        path: 'city',
        alias: 'gc',
      },
      {
        path: 'gc.province',
        alias: 'gp',
      },
    ],
  };
  const queryString = filters ? convertQueryCommandToQueryString(filters) : '';
  const result = await propertyRequest.get('', queryString);
  const buildingData = result?.data && result.data[0]
    ? result.data[0] as IBuildings
    : undefined;
  return buildingData;
};

/**
 * Get building types that are unique to the client account.
 * @returns IBuildingTypes[]
 */
export const getClientBuildingTypes = async (): Promise<IBuildingTypes[]> => {
  const query: IQueryCommandOptions = {
    select: ['id'],
    relations: [{
      path: 'buildingType',
      alias: 'bt',
    }],
    where: [Not(Equal('status', 'deleted'))],
    orderBy: [{ field: 'bt.name', direction: 'asc' }],
    limit: 9999,
  };

  const response: IApiResponse<IBuildings[], unknown> = await propertyRequest.get(`/${convertQueryCommandToQueryString(query)}`);

  const buildingTypes = (response.data ?? []).reduce<IBuildingTypes[]>((acc, cur) => [
    ...acc,
    ...(cur.buildingType && !acc.some((bt) => bt.id === cur.buildingType?.id) ? [ cur.buildingType ] : []),
  ], []);

  return buildingTypes;
};

export default {
  getById,
  getByIdV2,
  get,
  getV2,
  save,
  saveMany,
  remove,
  getUnitModels,
  getUnits,
  getPhotos,
  getSettings,
  getSettingsV2,
  getPropertiesListWithEnabledUnitCount,
  getRelations,
  getForSubheader,
  getUnitDataForSubheader,
  getPropertiesList,
  saveRelation,
  saveUploadedFileResults,
  deleteRelation,
  getPropertyOptions,
  processOwnershipGroup,
  getBuildingTypes,
  getExampleBuilding,
  getClientBuildingTypes,
};
